import { useCallback, useEffect, useState, useMemo } from 'react'
import TeacherService from '../../services/teacher-service'
import GrileService from '../../services/grile-service'
import { DIFFICULTIES_INTERVALS } from '../../constants/difficulties'
import {
  filterGridsByDifficulty,
  handleFilterByGrilaType,
  handleVerifyTagsInFilter,
} from '../../utils'
import useGetAllTags from '../../hooks/useGetAllTags'
import { useAuthContext } from '../../context'

const useCreateSubject = () => {
  const [currentSubject, setCurrentSubject] = useState([])
  const [grids, setGrids] = useState([])
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [defaultGrids, setDefaultGrids] = useState([])
  const [allTags, setAllTags] = useState([])
  const [filteredTags, setFilteredTags] = useState([])
  const [filteredCorrect, setFilteredCorrect] = useState([])
  const [filtersPopupOpened, setFiltersPopupOpened] = useState(false)
  const [searchedValue, setSearchedValue] = useState('')
  const [allSources, setAllSources] = useState([])
  const [selectedSource, setSelectedSource] = useState('Toate')
  const [difficultyFilters, setDifficultyFilters] = useState([
    {
      key: 'easy',
      label: 'Usoară',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.EASY.minValue,
      maxValue: DIFFICULTIES_INTERVALS.EASY.maxValue,
    },
    {
      key: 'medium',
      label: 'Medie',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.MEDIUM.minValue,
      maxValue: DIFFICULTIES_INTERVALS.MEDIUM.maxValue,
    },
    {
      key: 'hard',
      label: 'Grea',
      checked: true,
      minValue: DIFFICULTIES_INTERVALS.HARD.minValue,
      maxValue: DIFFICULTIES_INTERVALS.HARD.maxValue,
    },
  ])
  const [grileTypes, setGrileTypes] = useState([
    {
      id: 'single',
      name: 'Cu răspuns unic',
      checked: true,
    },
    {
      id: 'multiple',
      name: 'Cu răspuns multiplu',
      checked: true,
    },
  ])
  const { coursesWithChapters } = useGetAllTags()
  const { userDetails } = useAuthContext()
  const addGrid = gridId => {
    setCurrentSubject([...currentSubject, gridId])
  }

  const removeGrid = gridId => {
    setCurrentSubject(currentSubject.filter(id => id !== gridId))
  }
  const isGridInCurrentSubject = useCallback(
    grid => {
      for (const gridId of currentSubject) {
        if (gridId === grid.id) {
          return true
        }
      }

      return false
    },

    [currentSubject],
  )
  const availableGrids = useMemo(() => {
    return grids.filter(grid => !isGridInCurrentSubject(grid))
  }, [grids, isGridInCurrentSubject])

  const findGridCourse = tags => {
    for (const tag of tags) {
      for (const course in coursesWithChapters) {
        const coursesIds = coursesWithChapters[course].map(
          mappedCourse => mappedCourse.id,
        )
        if (coursesIds.includes(tag)) {
          return course
        }
      }
    }
    return null
  }

  const getCreatedSubject = useCallback(() => {
    return defaultGrids
      .filter(grid => isGridInCurrentSubject(grid))
      .map(grid => ({
        ...grid,
        json: JSON.parse(grid.json),
      }))
  }, [defaultGrids, isGridInCurrentSubject])
  const handleChange = useCallback((event, newValue) => {
    setActiveTab(newValue)
  }, [])

  const closeModal = () => setFiltersPopupOpened(false)

  const handleSelectDifficulty = useCallback(
    index => {
      const copy = [...difficultyFilters]
      // const isOnlyOneChecked =
      //   copy.filter(difficulty => difficulty.checked).length === 1

      // if (isOnlyOneChecked && copy[index].checked) {
      //   return
      // }

      copy[index].checked = !copy[index].checked
      setDifficultyFilters(copy)
    },
    [difficultyFilters],
  )

  const handleSearchValue = useCallback(value => {
    setSearchedValue(value)
  }, [])

  const sortedGrids = useCallback(
    grids => {
      const userEmail = userDetails?.email

      const ret = grids.toSorted((a, b) => {
        if (a.creator_email === userEmail && b.creator_email === userEmail) {
          return a.id - b.id
        }

        if (a.creator_email === userEmail) {
          return -1
        }

        if (b.creator_email === userEmail) {
          return 1
        }

        return a.id - b.id
      })

      return ret
    },
    [userDetails],
  )

  useEffect(() => {
    const fetcher = async () => {
      try {
        setIsLoading(true)
        const gridsResponse = await TeacherService.grile()
        const tags = await GrileService.getAllFilters()
        setAllTags(tags?.filters)
        setGrids(sortedGrids(gridsResponse))
        setDefaultGrids(gridsResponse)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    if (grids?.length === 0 && activeTab === 0) {
      fetcher()
    }
  }, [activeTab, grids, sortedGrids])

  useEffect(() => {
    const filteredGrids = filterGridsByDifficulty(
      defaultGrids,
      difficultyFilters,
    )
    const gridsFilteredByTags = filteredGrids?.filter(grid =>
      handleVerifyTagsInFilter(grid, filteredTags),
    )

    const filteredGridsBySearchedValue = gridsFilteredByTags?.filter(grid =>
      grid.json.includes(searchedValue),
    )
    const filteredGridsByGrileType = handleFilterByGrilaType(
      filteredGridsBySearchedValue,
      grileTypes,
    )
    setGrids(sortedGrids(filteredGridsByGrileType))
  }, [
    difficultyFilters,
    filteredTags,
    searchedValue,
    grileTypes,
    defaultGrids,
    sortedGrids,
  ])

  const subject = getCreatedSubject().map(grid => {
    return {
      ...grid,
      course: findGridCourse(grid.tags),
    }
  })

  const onSuccessCreateSubject = () => {
    setCurrentSubject([])
    setFiltersPopupOpened(false)
    setActiveTab(0)
  }

  const handleChangeSource = event => {
    const value = event.target.value
    setSelectedSource(value)
  }

  const handleChangeChaptersByCourse = (course, isSelected) => {
    const newFilteredTags = [...filteredTags]
    if (isSelected) {
      for (let index = 0; index < coursesWithChapters[course].length; index++) {
        if (newFilteredTags.includes(coursesWithChapters[course][index].id)) {
          newFilteredTags.splice(
            newFilteredTags.indexOf(coursesWithChapters[course][index].id),
            1,
          )
        }
      }
    } else {
      for (let index = 0; index < coursesWithChapters[course].length; index++) {
        if (!newFilteredTags.includes(coursesWithChapters[course][index].id)) {
          newFilteredTags.push(coursesWithChapters[course][index].id)
        }
      }
    }
    setFilteredTags(newFilteredTags)
    localStorage.setItem('tagFilters', newFilteredTags.join(','))
  }
  const handleChangeGrileTypes = index => {
    if (grileTypes[index].id === 'single') {
      localStorage.setItem(
        'singleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    if (grileTypes[index].id === 'multiple') {
      localStorage.setItem(
        'multipleChoice',
        JSON.stringify(!grileTypes[index].checked),
      )
    }
    setGrileTypes(prevTypes =>
      prevTypes.map((type, i) =>
        i === index ? { ...type, checked: !type.checked } : type,
      ),
    )
  }

  useEffect(() => {
    ;(async () => {
      try {
        const sourcesResponse = await GrileService.getAllSources()
        setAllSources(sourcesResponse)
      } catch (error) {
        console.error(error)
      }
    })()
  }, [])

  return {
    handleChange,
    activeTab,
    getCreatedSubject,
    removeGrid,
    addGrid,
    grids,
    currentSubject,
    availableGrids,
    allTags,
    isLoading,
    difficultyFilters,
    setDifficultyFilters,
    handleSelectDifficulty,
    filteredTags,
    setFilteredTags,
    filteredCorrect,
    setFilteredCorrect,
    filtersPopupOpened,
    setFiltersPopupOpened,
    handleSearchValue,
    searchedValue,
    subject,
    closeModal,
    onSuccessCreateSubject,
    handleChangeChaptersByCourse,
    allSources,
    handleChangeSource,
    selectedSource,
    grileTypes,
    setGrileTypes,
    handleChangeGrileTypes,
  }
}

export default useCreateSubject
